import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API, formatApiError, formatCurrency, formatDateTime } from "@/lib/api";
import { log } from "@/lib/log";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ReceiptViewer } from "@/components/ReceiptViewer";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Lock, FileText, ExternalLink, Mail, Copy, Download, Bell, Send } from "lucide-react";
import { toast } from "sonner";

export default function ExpenseDetail() {
  const { id } = useParams();
  const [exp, setExp] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [submittingAccounts, setSubmittingAccounts] = useState(false);

  const submitToAccountsSoftware = async () => {
    setSubmittingAccounts(true);
    setError("");
    try {
      const { data } = await api.post(`/expenses/${id}/submit-accounts-software`);
      toast.success(`${data.count} receipt${data.count === 1 ? "" : "s"} sent to ${data.email}`);
      await reload();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmittingAccounts(false);
    }
  };

  const sendReminder = async () => {
    setReminding(true);
    setError("");
    try {
      const { data } = await api.post(`/expenses/${id}/remind`);
      toast.success(`Reminder sent to ${data.reminded} pending approver${data.reminded === 1 ? "" : "s"}`);
      await reload();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setReminding(false);
    }
  };

  const downloadCertificate = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/expenses/${id}/audit-certificate.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `audit-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      log.error("Failed to download certificate:", err);
      setError(formatApiError(err));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const reload = useCallback(async () => {
    try {
      const [e, a] = await Promise.all([
        api.get(`/expenses/${id}`),
        api.get(`/expenses/${id}/audit`),
      ]);
      setExp(e.data);
      setAudit(a.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const receipts = exp?.receipts && exp.receipts.length
    ? exp.receipts
    : (exp?.receipt_filename ? [{ filename: exp.receipt_filename, content_type: exp.receipt_content_type }] : []);

  const finalized = exp && (exp.status === "approved" || exp.status === "rejected");

  const decisionsCount = useMemo(() => {
    if (!exp) return { approved: 0, rejected: 0, pending: 0 };
    return exp.approvers.reduce((acc, a) => { acc[a.decision] = (acc[a.decision] || 0) + 1; return acc; }, { approved: 0, rejected: 0, pending: 0 });
  }, [exp]);

  if (loading) {
    return (
      <Layout>
        <div className="py-12 flex items-center justify-center text-ink-soft">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading expense…
        </div>
      </Layout>
    );
  }
  if (error && !exp) return <Layout><div className="text-rose-800">{error}</div></Layout>;
  if (!exp) return null;

  return (
    <Layout>
      <div className="mb-6">
        <Button asChild variant="ghost" size="sm" className="text-ink-soft hover:text-forest-800 -ml-2" data-testid="back-to-dashboard">
          <Link to="/"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to expenses</Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-6 mb-8 animate-fade-up">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Expense</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 tracking-tight" data-testid="expense-payee">{exp.payee}</h1>
          <p className="text-sm text-ink-soft mt-2 max-w-xl">{exp.description}</p>
        </div>
        <div className="text-right">
          <div className="font-heading text-3xl sm:text-4xl text-forest-800" data-testid="expense-amount">
            {formatCurrency(exp.amount, exp.currency)}
          </div>
          <div className="mt-2 flex justify-end"><StatusBadge status={exp.status} /></div>
          <div className="text-[11px] uppercase tracking-wider text-ink-mute mt-2">
            {decisionsCount.approved}/{exp.approvers.length} approved{decisionsCount.rejected ? ` · ${decisionsCount.rejected} rejected` : ""}
          </div>
          <Button
            onClick={downloadCertificate}
            disabled={downloadingPdf}
            size="sm"
            variant="outline"
            data-testid="download-audit-certificate"
            className="mt-3 border-cream-200 hover:bg-cream-100 text-forest-800"
          >
            {downloadingPdf ? (<Loader2 className="h-3.5 w-3.5 animate-spin" />) : (<><Download className="h-3.5 w-3.5 mr-1.5" /> Audit certificate (PDF)</>)}
          </Button>
          {exp.status === "awaiting_approval" && (
            <Button
              size="sm" variant="outline"
              onClick={sendReminder}
              disabled={reminding}
              data-testid="send-reminder-button"
              className="mt-2 ml-2 border-cream-200 hover:bg-amber-50 hover:text-amber-800 hover:border-amber-200 text-forest-800"
            >
              {reminding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Bell className="h-3.5 w-3.5 mr-1.5" /> Send reminder</>}
            </Button>
          )}
          {exp.status === "approved" && (
            <Button
              size="sm" variant="outline"
              data-testid="resend-finance-button"
              onClick={async () => { try { await api.post(`/expenses/${id}/resend-finance`); await reload(); } catch (e) { setError(formatApiError(e)); } }}
              className="mt-2 ml-2 border-cream-200 hover:bg-cream-100 text-forest-800"
            >
              <Mail className="h-3.5 w-3.5 mr-1.5" /> Resend to finance
            </Button>
          )}
          {exp.status === "approved" && exp.accounts_software_email && (
            <Button
              size="sm" variant="outline"
              onClick={submitToAccountsSoftware}
              disabled={submittingAccounts}
              data-testid="submit-accounts-software-button"
              className="mt-2 ml-2 border-cream-200 hover:bg-forest-50 hover:text-forest-800 text-forest-800"
            >
              {submittingAccounts
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <><Send className="h-3.5 w-3.5 mr-1.5" /> {exp.accounts_software_submitted_at ? "Re-send to accounts software" : "Submit to accounts software"}</>}
            </Button>
          )}
        </div>
      </div>

      {exp.status === "approved" && exp.accounts_software_email && exp.accounts_software_submitted_at && (
        <div className="mb-4 text-xs text-ink-soft" data-testid="accounts-software-submitted-note">
          Last submitted to accounts software ({exp.accounts_software_email}) on {formatDateTime(exp.accounts_software_submitted_at)}. You can re-send anytime if it didn&apos;t arrive.
        </div>
      )}

      {error && (
        <div data-testid="action-error" className="mb-6 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Receipts */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-cream-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-cream-200 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold">
                {receipts.length > 1 ? `${receipts.length} receipts` : "Receipt"}
              </div>
            </div>
            <div data-testid="receipts-list">
              {receipts.length === 0 ? (
                <div className="p-6 text-center text-ink-soft text-sm">No receipts attached.</div>
              ) : (
                <ReceiptViewer
                  receipts={receipts}
                  testidPrefix="receipt"
                  fetchBlob={async (idx) => {
                    const res = await api.get(`/expenses/${id}/receipt/${idx}`, { responseType: "blob" });
                    return res.data;
                  }}
                />
              )}
            </div>
          </div>

          <div className="mt-4 bg-white border border-cream-200 rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold mb-3">Details</div>
            <dl className="text-sm space-y-2.5">
              <KV k="Submitted by" v={exp.created_by} />
              {exp.category && <KV k="Category" v={exp.category} />}
              <KV k="Sent to (when approved)" v={exp.finance_email || "—"} />
              <KV k="Accounts software" v={exp.accounts_software_email || "—"} />
              <KV k="Created" v={formatDateTime(exp.created_at)} />
              <KV k="Last updated" v={formatDateTime(exp.updated_at)} />
              <KV k="Currency" v={exp.currency} />
              <KV k="Expense ID" v={<code className="text-[11px]">{exp.id.slice(0, 8)}…</code>} />
            </dl>
          </div>
        </div>

        {/* Approvers + Audit */}
        <div className="lg:col-span-7 space-y-6">
          <section className="bg-white border border-cream-200 rounded-xl">
            <div className="px-5 py-4 border-b border-cream-200">
              <h2 className="font-heading text-lg text-forest-800">{exp.approvers.length} approvers</h2>
              <p className="text-xs text-ink-soft mt-0.5">
                All {exp.approvers.length} must approve. Any rejection stops the expense.
              </p>
            </div>
            <ul className="divide-y divide-cream-200" data-testid="approvers-list">
              {exp.approvers.map((a, idx) => (
                <li key={a.id} className="px-5 py-4" data-testid={`approver-row-${idx + 1}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-forest-900 truncate">{a.email}</div>
                      <div className="text-[11px] text-ink-mute uppercase tracking-wider mt-0.5">
                        {a.decision === "pending" ? "Awaiting decision" : `${a.decision} · ${formatDateTime(a.decided_at)}`}
                      </div>
                      {a.decision === "pending" && (a.reminders_sent || 0) > 0 && (
                        <div className="text-[11px] text-amber-700 mt-0.5 inline-flex items-center gap-1" data-testid={`reminder-count-${idx + 1}`}>
                          <Bell className="h-3 w-3" /> Reminded {a.reminders_sent}× · last {formatDateTime(a.last_reminder_at)}
                        </div>
                      )}
                      {a.comment && <div className="text-xs text-ink-soft mt-1 italic">“{a.comment}”</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={a.decision} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="px-5 py-3 border-t border-cream-200 bg-cream-50 text-[11px] text-ink-mute">
              <strong className="text-ink inline-flex items-center gap-1"><Mail className="h-3 w-3" /> Phase 2:</strong> each approver received a unique single-use email link. Their decision is recorded here as soon as they respond.
            </div>
          </section>

          {/* Dev convenience: when no email provider is configured, show the per-approver links here */}
          {exp._dev_approval_links && exp._dev_approval_links.length > 0 && (
            <section
              className="bg-amber-50 border border-amber-200 rounded-xl p-5"
              data-testid="dev-approval-links"
            >
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-md bg-amber-100 grid place-items-center shrink-0">
                  <Mail className="h-4 w-4 text-amber-800" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-base text-amber-900">No email provider configured</div>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    Add <code className="bg-amber-100 px-1 rounded">RESEND_API_KEY</code> in <code>/app/backend/.env</code> to send real emails. For now, copy each approver&apos;s link below and open it in an incognito tab to test the public approval flow.
                  </p>
                  <ul className="mt-4 space-y-2" data-testid="dev-link-list">
                    {exp._dev_approval_links.map((l) => (
                      <li key={l.link} className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-3 py-2">
                        <div className="text-xs font-medium text-forest-900 shrink-0 max-w-[40%] truncate">{l.approver_email}</div>
                        <code className="text-[11px] text-ink-soft flex-1 min-w-0 truncate">{l.link}</code>
                        <button
                          type="button"
                          data-testid={`copy-link-${l.approver_email}`}
                          onClick={() => navigator.clipboard?.writeText(l.link)}
                          className="text-[11px] inline-flex items-center gap-1 text-forest-700 hover:text-forest-800 shrink-0"
                        >
                          <Copy className="h-3 w-3" /> Copy
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          )}

          {/* Audit log */}
          <section className="bg-white border border-cream-200 rounded-xl">
            <div className="px-5 py-4 border-b border-cream-200 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg text-forest-800 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-forest-700" /> Audit log
                </h2>
                <p className="text-xs text-ink-soft mt-0.5">Append-only · {audit.length} record{audit.length === 1 ? "" : "s"}</p>
              </div>
            </div>
            <ol className="relative pl-5 py-5 pr-5" data-testid="audit-log-list">
              <span className="absolute left-7 top-5 bottom-5 w-px bg-cream-200" />
              {audit.map((a) => (
                <li key={a.id} className="relative pl-7 pb-5 last:pb-0" data-testid="audit-log-item">
                  <span className={`absolute left-[2px] top-1 h-3 w-3 rounded-full border-2 border-cream-50 ${dotColor(a.action)}`} />
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <div className="font-medium text-sm text-forest-900">{actionLabel(a.action)}</div>
                    <div className="text-[11px] uppercase tracking-wider text-ink-mute">{formatDateTime(a.timestamp)}</div>
                  </div>
                  <div className="text-xs text-ink-soft mt-0.5">
                    {a.actor_email} · {a.ip_address}
                  </div>
                  {a.comment && <div className="text-xs text-ink mt-1 italic">“{a.comment}”</div>}
                </li>
              ))}
            </ol>
          </section>
        </div>
      </div>
    </Layout>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[11px] uppercase tracking-wider text-ink-mute">{k}</dt>
      <dd className="text-sm text-ink text-right">{v}</dd>
    </div>
  );
}

function actionLabel(action) {
  const map = {
    "expense.created": "Expense created",
    "approver.assigned": "Approver assigned",
    "approver.approved": "Approver approved",
    "approver.rejected": "Approver rejected",
    "expense.approved": "Expense fully approved",
    "expense.rejected": "Expense rejected",
    "reminder.sent": "Reminder sent to approver",
    "reminder.failed": "Reminder failed to send",
    "email.sent": "Approval email sent",
    "email.failed": "Approval email failed",
    "finance.notified": "Sent to finance",
    "finance.resent": "Resent to finance",
    "accounts_software.submitted": "Submitted to accounts software",
    "accounts_software.email_sent": "Invoice emailed to accounts software",
    "accounts_software.email_failed": "Accounts software email failed",
  };
  return map[action] || action;
}

function dotColor(action) {
  if (action === "approver.approved" || action === "expense.approved") return "bg-emerald-500";
  if (action === "approver.rejected" || action === "expense.rejected") return "bg-rose-500";
  if (action === "expense.created") return "bg-forest-700";
  if (action.startsWith("reminder")) return "bg-amber-500";
  return "bg-ink-mute";
}
